// The prerendered homepage's two client-only shims, in a module of their own so a homepage rewrite can't lose them. Node-prerender-safe: react + react-router only — no store/Amplify/@shared (routes.ts), and nothing URL- or storage-derived is read during render, only inside the effect (same reason series-migration.tsx decides everything post-mount: `/` is baked with empty search params and no localStorage, so reading either at render hydration-mismatches).
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

// Amplify's own storage, read directly: the SPA's `authenticated` store flag drags zustand + Amplify into the prerender graph. Key format `CognitoIdentityServiceProvider.<clientId>.LastAuthUser`, cleared on sign-out.
const LAST_USER_SUFFIX = '.LastAuthUser';

/** Module-scope, deliberately: a stale/expired token is a false positive, and without a one-shot guard home → /app/j → the SPA's AuthRedirect sees `authenticated === false` → navigate("/") → home remounts → forwards again, an infinite client-side loop. */
let forwarded = false;

/** Test-only: the guard above outlives a test's unmount, so cases would run order-dependent. */
export function __resetHomeShims() { forwarded = false; }

/** True only when Amplify has both a last-authed username AND that user's cached idToken. */
function hasCognitoSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.endsWith(LAST_USER_SUFFIX)) continue;
      const username = localStorage.getItem(key);
      if (username && localStorage.getItem(`${key.slice(0, -LAST_USER_SUFFIX.length)}.${username}.idToken`)) return true;
    }
  } catch { /* private mode throws on localStorage access */ }
  return false;
}

/**
 * Post-mount only, so a crawler executes neither branch and indexes the full prerendered marketing HTML. A logged-out visitor sees marketing with no redirect; a logged-in one sees a brief marketing paint, then the app — that flash is accepted.
 * Shim A: logged in at `/` → into the app. Shim B (only if A didn't fire): the legacy `/?auth=<tab>` CTA link → the SPA's real `/auth` route.
 */
export function useHomeShims() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  useEffect(() => {
    if (forwarded) return;
    if (hasCognitoSession()) {
      forwarded = true;
      navigate('/app/j', { replace: true });
      return;
    }
    const tab = params.get('auth');
    if (tab) {
      forwarded = true;
      // `encodeURIComponent`: `params.get` already URL-decoded, so `?auth=x%26foo%3Dbar` would otherwise interpolate a second query param into the redirect.
      navigate(`/auth?tab=${encodeURIComponent(tab)}`, { replace: true });
    }
  }, []);
}
